import { onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * Tirar hacia abajo para recargar, en el teléfono.
 *
 * La app no hace scroll en el documento sino dentro de su propio panel
 * (`.shell__content`), así que el gesto del navegador nunca salta: en Chrome
 * porque el documento no se mueve, en Safari y en la app instalada porque no
 * existe. Quien trabaja con el teléfono lo da por hecho («bajo el dedo y se
 * actualiza»), así que se dibuja aquí, en el armazón, una vez para todas las
 * pantallas.
 *
 * Reglas para no recargar cuando no se quería:
 *   - solo con el dedo (`pointer: coarse`) y sin el teclado fuera;
 *   - solo si TODO lo que hay bajo el dedo hasta el panel está arriba del todo:
 *     una lista o una columna a media altura hace scroll, no recarga;
 *   - nunca donde arrastrar es la herramienta: mapas, firmas, deslizadores,
 *     rejillas de planificación (`[data-no-pull]` y los habituales);
 *   - el gesto tiene que ser vertical, y pasar un umbral antes de soltar;
 *   - con un formulario a medias, pregunta antes (`confirmDiscardingUnsaved`).
 */

export const PULL_THRESHOLD = 72
const MAX_PULL = 120
const START_SLOP = 8
const SKIP = 'canvas, input[type=range], [draggable=true], [data-no-pull], .leaflet-container, .maplibregl-map'

/** Si algo entre el dedo y el panel puede todavía subir: entonces no es nuestro. */
export function scrolledAwayFromTop(target, container) {
  let el = target instanceof Element ? target : null
  while (el && el !== container) {
    const style = getComputedStyle(el)
    const scrolls = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1
    if (scrolls && el.scrollTop > 0) return true
    el = el.parentElement
  }
  return container.scrollTop > 0
}

/**
 * @param {import('vue').Ref<HTMLElement|null>} paneRef  el panel que hace scroll
 * @param {{ onRefresh: () => Promise<void>|void, canStart?: () => boolean }} options
 */
export function usePullToRefresh(paneRef, { onRefresh, canStart = () => true } = {}) {
  const pull = ref(0)
  const armed = ref(false)
  const refreshing = ref(false)

  let startY = 0
  let startX = 0
  let tracking = false
  let pulling = false

  const touchDevice = () => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches

  function reset() {
    tracking = false
    pulling = false
    pull.value = 0
    armed.value = false
  }

  function onStart(event) {
    if (refreshing.value || event.touches.length !== 1 || !touchDevice()) return
    if (document.documentElement.classList.contains('keyboard-open')) return
    if (!canStart()) return
    const pane = paneRef.value
    const target = event.target
    if (!pane || (target instanceof Element && target.closest(SKIP))) return
    if (scrolledAwayFromTop(target, pane)) return
    tracking = true
    pulling = false
    startY = event.touches[0].clientY
    startX = event.touches[0].clientX
  }

  function onMove(event) {
    if (!tracking) return
    const dy = event.touches[0].clientY - startY
    const dx = Math.abs(event.touches[0].clientX - startX)
    if (!pulling) {
      // Hacia arriba o de lado es scroll normal o un deslizamiento: se suelta.
      if (dy < START_SLOP) { if (dy < 0 || dx > START_SLOP) tracking = false; return }
      if (dx > dy) { tracking = false; return }
      pulling = true
    }
    // Resistencia: el círculo va a la mitad que el dedo y tiene tope.
    pull.value = Math.min(MAX_PULL, Math.max(0, (dy - START_SLOP) * 0.5))
    armed.value = pull.value >= PULL_THRESHOLD
    // Que el panel no rebote debajo mientras se tira.
    if (event.cancelable) event.preventDefault()
  }

  async function onEnd() {
    if (!tracking) return
    const go = pulling && armed.value
    if (!go) { reset(); return }
    tracking = false
    pulling = false
    refreshing.value = true
    pull.value = PULL_THRESHOLD
    try {
      await onRefresh()
    } finally {
      // Si la recarga no llega a ocurrir (se canceló el «descartar»), vuelve.
      refreshing.value = false
      reset()
    }
  }

  onMounted(() => {
    const pane = paneRef.value
    if (!pane) return
    pane.addEventListener('touchstart', onStart, { passive: true })
    pane.addEventListener('touchmove', onMove, { passive: false })
    pane.addEventListener('touchend', onEnd, { passive: true })
    pane.addEventListener('touchcancel', reset, { passive: true })
  })

  onBeforeUnmount(() => {
    const pane = paneRef.value
    if (!pane) return
    pane.removeEventListener('touchstart', onStart)
    pane.removeEventListener('touchmove', onMove)
    pane.removeEventListener('touchend', onEnd)
    pane.removeEventListener('touchcancel', reset)
  })

  return { pull, armed, refreshing }
}
